import { useI18n } from '@affine/i18n';
import { useCallback, useMemo, useState } from 'react';

import { AudioBlockEmbedded } from './audio/audio-block';
import { AttachmentPreviewErrorBoundary } from './error';
import {
  HtmlAttachmentPresentation,
  HtmlAttachmentPreview,
} from './html/html-viewer';
import { AttachmentPresentationDialog } from './presentation-dialog';
import { PDFPresentation } from './pdf/pdf-presentation';
import { PDFViewerEmbedded } from './pdf/pdf-viewer-embedded';
import type { AttachmentViewerProps } from './types';
import { getAttachmentType } from './utils';

type PresentationState =
  | { type: 'pdf'; page: number }
  | { type: 'html' }
  | null;

// Embed view
export const AttachmentEmbedPreview = ({ model }: AttachmentViewerProps) => {
  const t = useI18n();
  const [presentation, setPresentation] = useState<PresentationState>(null);
  const closePresentation = useCallback(() => setPresentation(null), []);
  const openPdfPresentation = useCallback(
    (page: number) => setPresentation({ type: 'pdf', page }),
    []
  );
  const openHtmlPresentation = useCallback(
    () => setPresentation({ type: 'html' }),
    []
  );
  const attachmentType = getAttachmentType(model);
  const element = useMemo(() => {
    switch (attachmentType) {
      case 'pdf':
        return (
          <PDFViewerEmbedded
            model={model}
            onPresent={openPdfPresentation}
            presentLabel={t['com.affine.blocksuite.presentation.present']()}
          />
        );
      case 'html':
        return (
          <HtmlAttachmentPreview
            model={model}
            title={model.props.name}
            onPresent={openHtmlPresentation}
            labels={{
              present: t['com.affine.blocksuite.presentation.present'](),
              enterFullscreen:
                t['com.affine.blocksuite.presentation.enter-full-screen'](),
              open: t['com.affine.attachment.presentation.open-html'](),
              close: t['com.affine.attachment.presentation.close'](),
              loading: t['com.affine.attachment.presentation.loading-html'](),
              reload: t['com.affine.blocksuite.toolbar.label.reload'](),
              failedToLoad:
                t['com.affine.attachment.presentation.html-load-failed'](),
              fullscreenFailed:
                t['com.affine.attachment.presentation.fullscreen-failed'](),
            }}
          />
        );
      case 'audio':
        return <AudioBlockEmbedded model={model} />;
      default:
        return null;
    }
  }, [attachmentType, model, openHtmlPresentation, openPdfPresentation, t]);
  return (
    <>
      <AttachmentPreviewErrorBoundary>{element}</AttachmentPreviewErrorBoundary>
      {presentation ? (
        <AttachmentPresentationDialog
          title={model.props.name}
          onClose={closePresentation}
        >
          <AttachmentPreviewErrorBoundary>
            {presentation.type === 'pdf' ? (
              <PDFPresentation
                model={model}
                initialPage={presentation.page}
                onClose={closePresentation}
                labels={{
                  close: t['com.affine.attachment.presentation.close'](),
                  previousPage:
                    t['com.affine.blocksuite.presentation.previous'](),
                  nextPage: t['com.affine.blocksuite.presentation.next'](),
                  enterFullscreen:
                    t['com.affine.blocksuite.presentation.enter-full-screen'](),
                  exitFullscreen:
                    t['com.affine.blocksuite.presentation.exit-full-screen'](),
                  loading:
                    t['com.affine.attachment.presentation.loading-pdf'](),
                  loadError:
                    t['com.affine.attachment.presentation.pdf-load-failed'](),
                }}
              />
            ) : (
              <HtmlAttachmentPresentation
                model={model}
                title={model.props.name}
                onClose={closePresentation}
                labels={{
                  close: t['com.affine.attachment.presentation.close'](),
                  present: t['com.affine.blocksuite.presentation.present'](),
                  enterFullscreen:
                    t['com.affine.blocksuite.presentation.enter-full-screen'](),
                  open: t['com.affine.attachment.presentation.open-html'](),
                  loading:
                    t['com.affine.attachment.presentation.loading-html'](),
                  reload: t['com.affine.blocksuite.toolbar.label.reload'](),
                  failedToLoad:
                    t['com.affine.attachment.presentation.html-load-failed'](),
                  fullscreenFailed:
                    t['com.affine.attachment.presentation.fullscreen-failed'](),
                }}
              />
            )}
          </AttachmentPreviewErrorBoundary>
        </AttachmentPresentationDialog>
      ) : null}
    </>
  );
};
